<template>
  <div ref="quoteDiv" class="quote" :class="cssClasses">
    <img
      ref="imageAtTop"
      v-if="!isImageAtBottom"
      :src="imageSrc"
      :alt="imageAlternateText"
      class="quote-image"
      :style="{ 'shape-outside': 'url(' + imageSrcForShapeOutside + ')' }"
    />
    <div class="quote-text" v-html="quoteText"></div>
    -
    <div class="quote-source" v-html="quoteSource"></div>
    <img
      ref="imageAtBottom"
      v-if="isImageAtBottom"
      :src="imageSrc"
      :alt="imageAlternateText"
      class="quote-image"
      :style="{ 'shape-outside': 'url(' + imageSrcForShapeOutside + ')' }"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref } from "vue";
import { debug as d } from "debug";
import cms from "../../services/cms/cms";
import {
  CmsMediaLink,
  CmsQuote,
  CmsQuoteEmbed,
  CmsQuoteHorizontalBarPosition,
  CmsQuoteImagePosition,
} from "../../services/cms/cms-type-definitions";
import RenderWaiter from "../../services/utilities/render-waiter";

const debug = d("cs.CmsQuoteComponent");
const props = defineProps<{
  slice: CmsQuoteEmbed | null;
  renderWaiter: RenderWaiter;
}>();

const quoteImagePositionCssClassMap = [
  CmsQuoteImagePosition.left,
  CmsQuoteImagePosition.right,
  CmsQuoteImagePosition.bottom,
].reduce((theMap, enumValue) => {
  theMap[enumValue] = `quote-image-${enumValue}`;
  return theMap;
}, {});
const quoteHorizontalBarPositionCssClassMap = [
  CmsQuoteHorizontalBarPosition.top,
  CmsQuoteHorizontalBarPosition.bottom,
].reduce((theMap, enumValue) => {
  theMap[enumValue] = `quote-bar-at-${enumValue}`;
  return theMap;
}, {});

// template refs
const quoteDiv = ref<HTMLDivElement | null>(null);
const imageAtTop = ref<HTMLImageElement | null>(null);
const imageAtBottom = ref<HTMLImageElement | null>(null);

const quoteDoc = ref<CmsQuote | null>(null);
const cssClasses = ref({});
const quoteText = ref("");
const quoteSource = ref("");
const imageSrc = ref("");
const imageAlternateText = ref("");
const isImageAtBottom = ref(false);

const imageSrcForShapeOutside = computed(() => {
  return `${imageSrc.value || ""}?t=${Date.now().toString()}`;
});

const imageElement = computed(() => {
  return isImageAtBottom.value ? imageAtBottom.value : imageAtTop.value;
});

function createCssClassesObject(
  imagePosition: CmsQuoteImagePosition,
  barPosition: CmsQuoteHorizontalBarPosition,
) {
  const cssClassesArray: Array<string> = [];
  if (quoteImagePositionCssClassMap[imagePosition]) {
    cssClassesArray.push(quoteImagePositionCssClassMap[imagePosition]);
  }
  if (quoteHorizontalBarPositionCssClassMap[barPosition]) {
    cssClassesArray.push(quoteHorizontalBarPositionCssClassMap[barPosition]);
  }
  const cssClassesObject = cssClassesArray.reduce((classesObj, className) => {
    classesObj[className] = true;
    return classesObj;
  }, {});
  return cssClassesObject;
}

function deferImageTextWrapping() {
  setTimeout(() => {
    if (props.renderWaiter) {
      props.renderWaiter.notifyReady(undefined);
    }
  }, 0);
}

function setImageWidthAndTextWrapping(image: CmsMediaLink) {
  const rawImageWidthPx = image?.width ?? Number.NaN;
  const widthOfDiv = quoteDiv.value?.clientWidth || 1440;
  const imageElementWidth = !Number.isNaN(rawImageWidthPx)
    ? `${(rawImageWidthPx / 1440) * widthOfDiv}px`
    : "100%";
  const imgElement = imageElement.value as HTMLElement;
  imgElement.style.width = imageElementWidth;

  deferImageTextWrapping();
}

function onWindowResize() {
  debug("onWindowResize");
  if (quoteDoc.value && quoteDoc.value.data.image) {
    setImageWidthAndTextWrapping(quoteDoc.value.data.image);
  }
}

onMounted(() => {
  debug("mounted");
  if (props.slice && props.slice.primary.document && props.slice.primary.document.id) {
    if (props.renderWaiter) {
      props.renderWaiter.waitFor(undefined);
    }
    cms.getQuote(props.slice.primary.document.id).then((quoteDocResponse) => {
      debug("Got quote doc", quoteDocResponse);
      quoteDoc.value = quoteDocResponse as CmsQuote;
      const properties = quoteDoc.value.data;
      quoteText.value = cms.asHtml(properties.content);
      quoteSource.value = cms.asHtml(properties.source);

      if (properties.image_position !== CmsQuoteImagePosition.none && properties.image.url) {
        imageSrc.value = "";
        imageAlternateText.value = "Image to accompany quote";
        isImageAtBottom.value = properties.image_position === CmsQuoteImagePosition.bottom;

        setTimeout(() => {
          imageSrc.value = properties.image.url;
          const imgElement = imageElement.value as HTMLElement;
          imgElement.onload = () => {
            setImageWidthAndTextWrapping(properties.image);
            window.addEventListener("resize", onWindowResize);
          };
        }, 0);
      } else {
        props.renderWaiter.notifyReady(undefined);
      }

      cssClasses.value = createCssClassesObject(
        properties.image_position,
        properties.horizontal_bar_position,
      );
    });
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", onWindowResize);
});
</script>

<style lang="less">
@import (reference) "../../styles/bootstrap/variables.less";

.quote {
  position: relative;
  min-height: 120px;
  margin: 45px auto;
  font-size: 14px;
  overflow: hidden;
  font-size: 6px;
  max-width: 530px;

  p {
    margin: 0 !important;
    font-size: 23px;
    letter-spacing: 0.5px;
    line-height: 40px;
    min-height: 1em;
  }

  .quote-text {
    font-style: italic;
    font-weight: lighter;
  }

  .quote-source {
    white-space: nowrap;
  }

  .quote-source p::before {
    content: "- ";
  }

  .quote-image {
    shape-image-threshold: 0.5;
    shape-margin: 15px;
    margin: 0 !important;
  }

  &.quote-image-right > p {
    margin-left: 30px;
  }

  &.quote-image-right .quote-image {
    float: right;
    width: 100%;
  }

  &.quote-image-left .quote-image {
    float: left;
    width: 100%;
  }

  &.quote-image-bottom .quote-image {
    width: 100%;
  }

  &.quote-image-left .sandbag {
    margin-right: 10px;
  }

  &.quote-image-right .sandbag {
    margin-left: 10px;
  }

  &.quote-image-left .quote-source {
    text-align: right;
  }

  &.quote-bar-at-bottom .quote-source {
    position: relative;
  }

  &.quote-bar-at-bottom .quote-source {
    padding-bottom: 20px;

    &::after {
      content: "";
      position: absolute;
      bottom: 10px;
      left: 0;
      width: 100%;
      border-bottom: 2px solid #6dc8bf;
    }
  }

  &.quote-bar-at-top {
    padding-top: 15px;
  }

  &.quote-bar-at-top .quote-text::before {
    content: "";
    position: absolute;
    top: 0px;
    left: 0;
    width: 100%;
    border-bottom: 2px solid #6dc8bf;
  }
}

@media (max-width: @screen-xxs-max) {
  .quote p {
    font-size: 4vw;
    line-height: 7vw;
  }
}
</style>
